import { Address, beginCell } from '@ton/core';

/** TEP-74 jetton transfer op. */
const JETTON_TRANSFER_OP = 0xf8a7ea5;

export type GramJettonTransferParams = {
  jettonWalletAddress: string;
  destination: string;
  responseAddress: string;
  amountUnits: bigint;
  gasNanotons: bigint;
  forwardTonAmount?: bigint;
  comment?: string;
};

/**
 * Build a TonConnect message that transfers GRAM jetton units to `destination`.
 * `messages[0].amount` is TON gas only — never the GRAM quantity.
 */
export function buildGramJettonTransferMessage(params: GramJettonTransferParams): {
  address: string;
  amount: string;
  payload: string;
} {
  const forwardPayload = params.comment
    ? beginCell().storeUint(0, 32).storeStringTail(params.comment).endCell()
    : beginCell().endCell();

  const body = beginCell()
    .storeUint(JETTON_TRANSFER_OP, 32)
    .storeUint(0, 64)
    .storeCoins(params.amountUnits)
    .storeAddress(Address.parse(params.destination))
    .storeAddress(Address.parse(params.responseAddress))
    .storeBit(0)
    .storeCoins(params.forwardTonAmount ?? 1n)
    .storeBit(1)
    .storeRef(forwardPayload)
    .endCell();

  return {
    address: Address.parse(params.jettonWalletAddress).toString(),
    amount: params.gasNanotons.toString(),
    payload: body.toBoc().toString('base64'),
  };
}

type TonapiJettonBalance = {
  wallet_address?: { address?: string };
  jetton?: { address?: string };
};

/**
 * Resolve the player's GRAM jetton-wallet address via TonAPI.
 * Returns null when the wallet has no GRAM jetton account (cannot safely send).
 */
export async function resolveGramJettonWallet(
  ownerAddress: string,
  jettonMaster: string,
): Promise<string | null> {
  const owner = Address.parse(ownerAddress).toRawString();
  const master = Address.parse(jettonMaster).toRawString();
  const url = `https://tonapi.io/v2/accounts/${encodeURIComponent(owner)}/jettons/${encodeURIComponent(master)}`;
  const res = await fetch(url, { headers: { Accept: 'application/json' } });
  if (res.status === 404) return null;
  if (!res.ok) throw new Error('jetton_wallet_lookup_failed');
  const data = (await res.json()) as TonapiJettonBalance;
  const wallet = data.wallet_address?.address;
  if (!wallet) return null;
  return Address.parse(wallet).toString();
}
